import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@emotion/core';
import { gridSize } from '@atlaskit/theme/constants';
import { getBackgroundColor, getTextColor, TRANSITION_DURATION } from '../styles';
const iconStyles = css({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
  flexDirection: 'column',
  '@media screen and (forced-colors: active)': {
    fill: 'CanvasText',
    filter: 'grayscale(100%)'
  }
});
const visibilityStyles = css({
  overflow: 'hidden',
  transition: `max-height ${TRANSITION_DURATION}`
});
const contentStyles = css({
  display: 'flex',
  margin: 'auto',
  padding: gridSize() * 1.5,
  alignItems: 'center',
  justifyContent: 'center',
  color: 'currentColor',
  fontWeight: 500,
  textAlign: 'center',
  transition: `color ${TRANSITION_DURATION}`,
  // eslint-disable-next-line @repo/internal/styles/no-nested-styles
  'a, a:visited, a:hover, a:focus, a:active': {
    color: 'currentColor',
    textDecoration: 'underline'
  }
});
const cappedWidthStyles = css({
  maxWidth: 876
});
const childrenTextStyles = css({
  padding: gridSize() / 2,
  flex: '0 1 auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});
const noOverflowStyles = css({
  textOverflow: 'none'
});
const containerStyles = css({
  maxHeight: 52,
  overflow: 'visible'
});
const scrollStyles = css({
  maxHeight: 88,
  overflow: 'scroll'
});

class Banner extends React.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      height: 0
    });

    _defineProperty(this, "getHeight", () => {
      if (this.containerRef) {
        this.setState({
          height: this.containerRef.clientHeight
        });
      }
    });

    _defineProperty(this, "innerRef", ref => {
      if (!ref) {
        return;
      }

      this.containerRef = ref;

      if (this.props.innerRef) {
        this.props.innerRef(ref);
      }

      this.getHeight();
    });

    _defineProperty(this, "getA11yProps", appearance => {
      const {
        isOpen
      } = this.props;
      const defaultProps = {
        'aria-hidden': !isOpen,
        role: 'alert'
      };

      if (appearance === 'announcement') {
        return { ...defaultProps,
          'aria-label': 'announcement',
          tabIndex: 0,
          role: 'region'
        };
      }

      return defaultProps;
    });
  }

  render() {
    const {
      appearance,
      children,
      icon,
      isOpen,
      testId
    } = this.props;
    const allyProps = this.getA11yProps(appearance);
    const isAnnouncementAppearance = appearance === 'announcement';
    const backgroundColorByAppearance = getBackgroundColor({
      appearance
    });
    const textColorByAppearance = getTextColor({
      appearance
    });
    return jsx("div", {
      css: visibilityStyles,
      style: {
        maxHeight: `${isOpen ? this.state.height : 0}px`
      }
    }, jsx("div", _extends({
      css: [containerStyles, isAnnouncementAppearance && scrollStyles],
      style: {
        backgroundColor: backgroundColorByAppearance,
        color: textColorByAppearance
      },
      ref: this.innerRef,
      "data-testid": testId
    }, allyProps), jsx("div", {
      css: [contentStyles, isAnnouncementAppearance && cappedWidthStyles]
    }, jsx("span", {
      css: iconStyles,
      style: {
        fill: backgroundColorByAppearance
      }
    }, icon), jsx("div", {
      css: [childrenTextStyles, isAnnouncementAppearance && noOverflowStyles]
    }, children))));
  }

} // eslint-disable-next-line @repo/internal/react/require-jsdoc


_defineProperty(Banner, "defaultProps", {
  appearance: 'warning',
  isOpen: false
});

export default Banner;